import React from 'react';
import { createPortal } from 'react-dom';
import { Card, Typography, Divider, Button, ConfigProvider, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { IconChevronUp } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';
import { IBookingRange } from './AvailabilityCalendar';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { isDailyRentalMode, lastRentalDay } from '../helpers/rentalPeriodHelper';
import { TEAL, SURFACE_1_SOFT } from '../theme/creamyTokens';
import { tealAccentTheme } from '../theme/appTheme';

const { Text, Title } = Typography;

/** Nested under a IBookingDraftLine -- e.g. the delivery line's own "why"
 * (fixed dispatch fee, distance x price/km, x2 legs when return pickup is
 * also requested). Never affects computeDraftTotal on its own; the parent
 * line's own `amount` already is their sum. */
export interface IBookingDraftSubLine {
  key: string;
  label: string;
  amount: number;
}

/** One line of the still-being-built booking -- only ever added once the
 * guest has actually picked that option (an unpicked Service/insurance/
 * accessory never shows up as a "pending" row here, unlike the old single-
 * page widget's own *CostEstimate placeholders). `amount` undefined is a
 * genuinely transient state (e.g. the accessory list is still loading), not
 * "dates not chosen yet" -- see IBookingDraftSummary's own doc comment for
 * why that case no longer happens now that dates are step 1. */
export interface IBookingDraftLine {
  key: string;
  label: string;
  providerLabel?: string;
  /** How this line's amount was arrived at, e.g. "200,00 €/día × 2 días" --
   * shown as small muted text under the label. Only for lines that really
   * are a rate times a quantity (the machine itself, accessories); a line
   * priced some other way just omits it. Exists because a guest reading a
   * bare total has no way to tell a two-day rental from a one-day one. */
  rateLabel?: string;
  amount?: number;
  subLines?: IBookingDraftSubLine[];
}

/** The in-progress booking, as known so far -- fed live from
 * HeavyDutyMachineryBookingWidget's own draft state on every render, unlike
 * BookingLinesSummary (IBookingSummaryData) which only ever renders a
 * Booking the backend has already created and priced. selectedRange is
 * undefined only during step 1 itself, before any dates are picked. */
export interface IBookingDraftSummary {
  headerTitle: string;
  selectedRange?: IBookingRange;
  rentalMode: BookingRentalMode;
  currencyCode: string;
  lines: IBookingDraftLine[];
}

/** Sum of every line with a known amount -- a line still loading (amount
 * undefined) is shown as "…" but simply excluded from the running total
 * rather than blanking the whole total out, since with dates picked first
 * this is now a brief transient state, not a long-lived "nothing known yet"
 * one. */
const computeDraftTotal = (lines: IBookingDraftLine[]): number | undefined => {
  const known = lines.filter((l) => l.amount != null);
  if (known.length === 0) return undefined;
  return known.reduce((sum, l) => sum + (l.amount ?? 0), 0);
};

/** The actual breakdown card -- shared as-is between the desktop sticky
 * sidebar and the mobile full-screen sheet below, rather than two divergent
 * layouts for the same content. */
export const BookingCheckoutSummaryCard: React.FC<{ summary: IBookingDraftSummary; language: string }> = ({ summary, language }) => {
  const { t } = useTranslation();
  const { selectedRange, rentalMode, lines, headerTitle, currencyCode } = summary;
  const total = computeDraftTotal(lines);

  const dateLines = selectedRange ? (
    isDailyRentalMode(rentalMode) ? (
      // endAt is exclusive (see rentalPeriodHelper), so the last day the
      // guest actually has the machine is the day before it -- printing
      // endAt raw here is what made a two-day rental read as ending the
      // day it really starts being free again.
      <>
        <Text style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_FIRST_DAY_LABEL)}: {formatFullDate(dayjs(selectedRange.startAt), language)}
        </Text>
        <br />
        <Text style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LAST_DAY_LABEL)}: {formatFullDate(lastRentalDay(selectedRange.endAt), language)}
        </Text>
      </>
    ) : rentalMode === 'DayPass' ? (
      <Text style={{ fontSize: 13 }}>{formatFullDate(dayjs(selectedRange.startAt), language)}</Text>
    ) : (
      <Text style={{ fontSize: 13 }}>
        {formatFullDate(dayjs(selectedRange.startAt), language)}, {dayjs(selectedRange.startAt).format('HH:mm')} – {dayjs(selectedRange.endAt).format('HH:mm')}
      </Text>
    )
  ) : (
    <Text type="secondary" style={{ fontSize: 13 }}>
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUMMARY_PICK_DATES_HINT)}
    </Text>
  );

  return (
    <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT }}>
      <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PAYMENT_SUMMARY_TITLE)}
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        {headerTitle}
      </Text>
      {dateLines}
      {lines.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          {lines.map((line) => (
            <div key={line.key} style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {line.label}
                  {line.providerLabel ? ` (${line.providerLabel})` : ''}
                </Text>
                <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  {line.amount != null ? formatCurrency(line.amount, currencyCode, language) : '…'}
                </Text>
              </div>
              {line.rateLabel && (
                <Text type="secondary" style={{ fontSize: 12, opacity: 0.75, display: 'block' }}>
                  {line.rateLabel}
                </Text>
              )}
              {line.subLines && line.subLines.length > 0 && (
                <div style={{ marginLeft: 12 }}>
                  {line.subLines.map((sub) => (
                    <div key={sub.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12, opacity: 0.75 }}>
                        {sub.label}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, opacity: 0.75, whiteSpace: 'nowrap' }}>
                        {formatCurrency(sub.amount, currencyCode, language)}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Divider style={{ margin: '8px 0' }} />
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PRICE_LABEL)}</Text>
        <Text strong style={{ fontSize: 18, color: TEAL }}>
          {total != null ? formatCurrency(total, currencyCode, language) : '—'}
        </Text>
      </div>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8, lineHeight: 1.4 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUMMARY_ESTIMATE_NOTICE)}
      </Text>
    </Card>
  );
};

interface BookingCheckoutSummaryBarProps {
  summary: IBookingDraftSummary;
  language: string;
  onViewBreakdown: () => void;
  actionLabel: string;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  onAction: () => void;
}

/** Mobile-only fixed bottom bar -- the always-visible "checkout" the guest
 * asked for, collapsed to just the running total (tap to expand the full
 * breakdown via BookingCheckoutSummarySheet) plus this step's own forward
 * action, so it never competes for space with MobileBottomNav (the booking
 * page hides that bar entirely, see HeavyDutyMachineryBookingPage's own
 * hideBottomNav) and can safely reuse the same fixed bottom slot/z-index. */
export const BookingCheckoutSummaryBar: React.FC<BookingCheckoutSummaryBarProps> = ({
  summary,
  language,
  onViewBreakdown,
  actionLabel,
  actionDisabled,
  actionLoading,
  onAction,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const total = computeDraftTotal(summary.lines);

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        background: token.colorBgContainer,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        padding: '10px 16px calc(10px + env(safe-area-inset-bottom, 0px))',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
      }}
    >
      <button
        type="button"
        onClick={onViewBreakdown}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUMMARY_VIEW_BREAKDOWN)}
          </Text>
          <IconChevronUp size={12} style={{ opacity: 0.6 }} />
        </span>
        <Text strong style={{ fontSize: 17, color: TEAL, display: 'block' }}>
          {total != null ? formatCurrency(total, summary.currencyCode, language) : '—'}
        </Text>
      </button>
      <ConfigProvider theme={tealAccentTheme}>
        <Button type="primary" shape="round" loading={actionLoading} disabled={actionDisabled} onClick={onAction} style={{ flexShrink: 0 }}>
          {actionLabel}
        </Button>
      </ConfigProvider>
    </div>
  );
};

/** Full breakdown, opened from BookingCheckoutSummaryBar's own "Ver
 * desglose" -- same portal + slide-up-sheet mechanics as
 * AccessoryPickerSheet, reused here instead of a new pattern. */
export const BookingCheckoutSummarySheet: React.FC<{ open: boolean; onClose: () => void; summary: IBookingDraftSummary; language: string }> = ({
  open,
  onClose,
  summary,
  language,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="booking-summary-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
          />
          <motion.div
            key="booking-summary-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2101,
              maxHeight: '80vh',
              overflowY: 'auto',
              background: token.colorBgContainer,
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
              padding: '10px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div style={{ width: 36, height: 4, borderRadius: 2, background: token.colorBorder, margin: '0 auto 16px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUMMARY_SHEET_TITLE)}
              </Title>
              <Button type="text" shape="circle" icon={<CloseOutlined />} onClick={onClose} />
            </div>
            <BookingCheckoutSummaryCard summary={summary} language={language} />
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
